import { compressImageFile } from '../../utils/imageScale';
import { uploadProfilePhoto } from './userDataStorage';
import { getProfileUserId, isValidProfileUserId } from '../../utils/identityUtils';
import { isNewExperience } from '../../utils/newExperience';

/**
 * uploadPickedPhoto — one home for "user picked a file → hosted photo URL".
 *
 * compress (200px @ 80%) → blob → Supabase Storage → public URL. On upload
 * failure it returns the compressed base64 instead of throwing: a photo in
 * hand is never lost to a bad network, and ensureProfileSynced migrates
 * base64 to Storage on the next load (the same path every profile photo
 * rides).
 *
 * Extracted 2026-08-16 when the QR photo sheet needed it and would otherwise
 * have been the third hand-rolled copy of this sequence.
 *
 * @param {File} file - from an <input type="file" accept="image/*">
 * @returns {Promise<string|null>} photo URL (Storage or base64), null if the
 *   file isn't an image or processing failed
 */
export const uploadPickedPhoto = async (file) => {
  if (!file || !file.type?.startsWith('image/')) return null;

  // SIGNUP redesign: one decode → the same thumb + a card-grade copy + photo
  // meta (lazy chunk — main never loads it). Dormant on main; with the flag
  // off, everything below runs exactly as before.
  if (isNewExperience()) {
    return import('../photo/cardPhotoUpload')
      .then(({ uploadPhotoWithCard }) => uploadPhotoWithCard(file))
      .catch((err) => {
        console.error('[PHOTO_UPLOAD] new-experience upload chunk failed:', err);
        return null;
      });
  }

  try {
    const compressedDataUrl = await compressImageFile(file);

    // Existing ID when valid, else a temp one (first-time profile, not yet
    // committed) — the filename only has to be stable, not final.
    const existingId = getProfileUserId();
    const profileUserId =
      existingId && isValidProfileUserId(existingId)
        ? existingId
        : `temp_${crypto.randomUUID().slice(0, 8)}`;

    const blob = await (await fetch(compressedDataUrl)).blob();
    const storageUrl = await uploadProfilePhoto(profileUserId, blob);

    if (!storageUrl) {
      console.warn('[PHOTO_UPLOAD] Upload failed, falling back to base64');
    }
    const photoUrl = storageUrl || compressedDataUrl;
    return photoUrl;
  } catch (error) {
    console.error('[PHOTO_UPLOAD] Error processing image:', error);
    return null;
  }
};
